/**
 * Envío AP-11 · tira para recortar el comienzo y el final de un clip.
 * Dos manijas (mouse, dedo o flechas del teclado) y el cabezal de reproducción.
 */
import { useCallback, useRef } from 'react';
import { clampTrim, fmtSec } from '../../utils/cameraCapture';

const STEP = 0.1;

export default function VideoTrimmer({ total, start, end, playhead = null, dest = null, onChange, onSeek }) {
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const T = Math.max(total || 0, 0.01);
  const pct = (v) => `${(Math.min(Math.max(v, 0), T) / T) * 100}%`;

  const timeAt = useCallback((clientX) => {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r || !r.width) return 0;
    return Math.min(Math.max((clientX - r.left) / r.width, 0), 1) * T;
  }, [T]);

  const apply = useCallback((which, t) => {
    const next = which === 'start' ? clampTrim(t, Math.max(end, t + 1), T, null) : clampTrim(start, t, T, null);
    // el tope del destino se respeta moviendo la otra punta
    if (dest?.max && next.end - next.start > dest.max) {
      if (which === 'start') next.end = Math.round((next.start + dest.max) * 100) / 100;
      else next.start = Math.round((next.end - dest.max) * 100) / 100;
    }
    onChange?.(next);
  }, [start, end, T, dest, onChange]);

  const onDown = (which) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = which;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_e) { /* */ }
  };
  const onMove = (e) => { if (dragRef.current) apply(dragRef.current, timeAt(e.clientX)); };
  const onUp = () => { dragRef.current = null; };
  const onKey = (which) => (e) => {
    const d = e.key === 'ArrowLeft' ? -STEP : e.key === 'ArrowRight' ? STEP : 0;
    if (!d) return;
    e.preventDefault();
    apply(which, (which === 'start' ? start : end) + d * (e.shiftKey ? 10 : 1));
  };

  return (
    <div className="cam-trim" data-testid="cam-trim">
      <div className="cam-trim-head">
        <span className="cam-label">Recortar</span>
        <span className="cam-trim-sum">Tramo <b>{fmtSec(start)} → {fmtSec(end)}</b> · quedan <b className="is-accent" data-testid="cam-trim-len">{fmtSec(end - start)}</b></span>
      </div>
      <div
        ref={trackRef}
        className="cam-trim-track"
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onClick={(e) => { if (!dragRef.current) onSeek?.(timeAt(e.clientX)); }}
      >
        {Array.from({ length: 10 }).map((_, i) => <span key={i} className="cam-trim-cell" />)}
        <span className="cam-trim-dim" style={{ left: 0, width: pct(start) }} />
        <span className="cam-trim-dim" style={{ left: pct(end), right: 0 }} />
        <span className="cam-trim-sel" style={{ left: pct(start), width: `calc(${pct(end)} - ${pct(start)})` }} />
        {playhead != null && <span className="cam-trim-play" style={{ left: pct(playhead) }} />}
        <button type="button" className="cam-trim-handle" style={{ left: pct(start) }}
          role="slider" aria-label="Inicio del tramo" aria-valuemin={0} aria-valuemax={Math.round(T * 10) / 10}
          aria-valuenow={start} aria-valuetext={fmtSec(start)}
          onPointerDown={onDown('start')} onKeyDown={onKey('start')} onClick={(e) => e.stopPropagation()}
          data-testid="cam-trim-start" />
        <button type="button" className="cam-trim-handle" style={{ left: pct(end) }}
          role="slider" aria-label="Fin del tramo" aria-valuemin={0} aria-valuemax={Math.round(T * 10) / 10}
          aria-valuenow={end} aria-valuetext={fmtSec(end)}
          onPointerDown={onDown('end')} onKeyDown={onKey('end')} onClick={(e) => e.stopPropagation()}
          data-testid="cam-trim-end" />
      </div>
      <div className="cam-trim-foot"><span>0 s</span><span>Arrastrá las manijas para sacar el comienzo y el final</span><span>{fmtSec(T)}</span></div>
    </div>
  );
}
